export type DeviceMasterRow = Record<string, string>;

export type SortConfig = {
    key: string;
    direction: 'asc' | 'desc';
} | null;

export type ExtraColumnKey =
    | '__mode'
    | '__online'
    | '__fw'
    | '__modem_fw'
    | '__gnss_fw'
    | '__batt'
    | '__last'
    | '__tls'
    | '__lifecycle_lost'
    | '__lifecycle_decommissioned'
    | '__lifecycle_damaged'
    | '__lifecycle_errored'
    | '__lifecycle_note'
    | '__attr_race_count'
    | '__attr_waterproofed_by'
    | '__race_log'
    | '__debug_log'
    | '__batt_ma'
    | '__reason'
    | '__gga'
    | '__location_updated'
    | '__busy'
    | '__busy_from'
    | '__busy_reason'
    | '__last_cmd_id'
    | '__last_cmd_type'
    | '__last_cmd_target'
    | '__last_cmd_status'
    | '__last_cmd_ack_percent'
    | '__last_cmd_updated';

export const extraColumns: { key: ExtraColumnKey; label: string }[] = [
    { key: '__mode', label: 'Mode' },
    { key: '__online', label: 'Online?' },
    { key: '__fw', label: 'Firmware' },
    { key: '__modem_fw', label: 'Modem FW' },
    { key: '__gnss_fw', label: 'GNSS FW' },
    { key: '__batt', label: 'Battery (V)' },
    { key: '__last', label: 'Last Status' },
    { key: '__tls', label: 'Provisioned?' },
    { key: '__lifecycle_lost', label: 'Lost' },
    { key: '__lifecycle_decommissioned', label: 'Decommissioned' },
    { key: '__lifecycle_damaged', label: 'Damaged' },
    { key: '__lifecycle_errored', label: 'Errored' },
    { key: '__lifecycle_note', label: 'Lifecycle note' },
    { key: '__attr_race_count', label: 'Race count' },
    { key: '__attr_waterproofed_by', label: 'Waterproofed by' },
    { key: '__race_log', label: 'Race log (B)' },
    { key: '__debug_log', label: 'Debug log (B)' },
    { key: '__batt_ma', label: 'Battery (mA)' },
    { key: '__reason', label: 'Last State Reason' },
    { key: '__gga', label: 'Location' },
    { key: '__location_updated', label: 'Location Updated' },

    { key: '__busy', label: 'Busy?' },
    { key: '__busy_from', label: 'Busy From' },
    { key: '__busy_reason', label: 'Busy Reason' },

    { key: '__last_cmd_id', label: 'Command ID' },
    { key: '__last_cmd_type', label: 'Command Type' },
    { key: '__last_cmd_target', label: 'Command Target' },
    { key: '__last_cmd_status', label: 'Command Status' },
    { key: '__last_cmd_ack_percent', label: 'Command Progress' },
    { key: '__last_cmd_updated', label: 'Command Updated' },
];

export type CommandStatusFilter =
    | 'PENDING'
    | 'ACKED'
    | 'IN_PROGRESS'
    | 'SUCCEEDED'
    | 'FAILED'
    | 'TIMED_OUT'
    | 'CANCELLED';

export interface DeviceFiltersValue {
    online: 'any' | 'online' | 'offline';
    tls: 'any' | 'provisioned' | 'unprovisioned';
    /** Admin-only: fleet lifecycle in DynamoDB (missing row = in-service). */
    lifecycle: 'any' | 'active' | 'lost' | 'decommissioned' | 'damaged' | 'errored';
    mode: 'any' | 'app' | 'race' | 'charging' | 'busy' | 'offline';

    /** Filter by device ID prefix: Rear_ vs Front_. */
    deviceRole: 'any' | 'rear' | 'front';

    /** For rear devices: whether front_link_status indicates a paired front is linked. */
    frontLink: 'any' | 'linked' | 'unlinked';

    battMvMin: number | null;
    battMvMax: number | null;

    raceLogMin: number | null;
    raceLogMax: number | null;

    debugLogMin: number | null;
    debugLogMax: number | null;

    lastHeardFrom: string;
    lastHeardTo: string;

    /** Command type as stored in DynamoDB's COMMANDS_TABLE — the CMD_TYPE constants
     *  from fleet_common/device_states.py (REAR_/FRONT_-prefixed), not the lowercase
     *  `type` used by the separate submit-command request validation. Mirrors the
     *  CMD_REAR and CMD_FRONT constants in src/types/Commands.ts. */
    lastCmdType:
        | 'any'
        | 'REAR_FW_DWNLD'
        | 'FRONT_FW_DWNLD'
        | 'REAR_LOG_UPLD'
        | 'FRONT_LOG_UPLD'
        | 'REAR_DELETE_LOGS'
        | 'FRONT_DELETE_LOGS'
        | 'REAR_TOGGLE_LED'
        | 'FRONT_TOGGLE_LED'
        | 'REAR_APP_MODE'
        | 'REAR_RACE_MODE'
        | 'REAR_SLEEP'
        | 'REAR_REBOOT'
        | 'REAR_WINTER'
        | 'REAR_MODEM_DFOTA';

    // now a list of raw statuses
    lastCmdStatuses: (
        | 'PENDING'
        | 'ACKED'
        | 'IN_PROGRESS'
        | 'SUCCEEDED'
        | 'FAILED'
        | 'TIMED_OUT'
        | 'CANCELLED'
    )[];

    lastCmdTargetContains: string;
    lastCmdUpdatedFrom: string;
    lastCmdUpdatedTo: string;
    locationCenterLat: number | null;
    locationCenterLng: number | null;
    locationRadiusKm: number | null;
}

/** Canonical "no filters applied" value. Shared so persistence, reset and the
 *  active-filter card all agree on what counts as a default. */
export const DEFAULT_DEVICE_FILTERS: DeviceFiltersValue = {
    online: 'any',
    tls: 'any',
    lifecycle: 'any',
    mode: 'any',
    deviceRole: 'any',
    frontLink: 'any',
    battMvMin: null,
    battMvMax: null,
    raceLogMin: null,
    raceLogMax: null,
    debugLogMin: null,
    debugLogMax: null,
    lastHeardFrom: '',
    lastHeardTo: '',
    lastCmdType: 'any',
    lastCmdStatuses: [],
    lastCmdTargetContains: '',
    lastCmdUpdatedFrom: '',
    lastCmdUpdatedTo: '',
    locationCenterLat: null,
    locationCenterLng: null,
    locationRadiusKm: null,
};


export type DeviceStatus = {
    fw?: string;
    /** Modem FW revision string (e.g. "EG915QNAAGAR05A05M08_..."), rear devices only. */
    modem_fw?: string;
    /** GNSS FW version string (e.g. "LC29HBANR11A06S_CSA2"), rear devices only. */
    gnss_fw?: string;
    /** Bytes on device. */
    race_log_size?: number;
    batt_ma?: number;
    race_mode?: number;
    last_state_reason?: string;
    batt_mv?: number;
    charging?: number;
    puck?: number;
    debug_log_size?: number;
    deviceId?: string;
    updatedAt?: number;
    /** Latitude in decimal degrees (from device status). */
    lat?: number;
    /** Longitude in decimal degrees (from device status). */
    lng?: number;
    /** Timestamp of last valid location update (epoch ms/sec). */
    last_location_updated_at?: number;

    busy?: number;
    busy_from?: number;
    busy_reason?: string;
    /** Set on Front_* rows updated via rear BLE proxy. */
    proxied_from_rear?: string;
    front_link_status?: string;

    /** Written by ACK handlers on every ACK; real-time standardised feedback text. */
    last_cmd_feedback?: string;
    /** Written by ACK handlers on every ACK; one of IN_PROGRESS / SUCCEEDED / FAILED / TIMED_OUT. */
    last_cmd_status?: string;

    [key: string]: unknown;
};
export type DeviceDetailsProps = {
    meta: Record<string, unknown>;
    metaColumns: string[];
    operatorScopedEventId?: string | null;

    status?: DeviceStatus;
    latestCommand?: import('./Commands').CommandDdbItem;
    onRunDeviceValidation?: (params: {
        eventId: string;
        raceId: string;
        deviceIds: string[];
    }) => Promise<void> | void;

    /** Sub / userId → display label (typically email); from `listUsers` + `buildGlobalUserIdToDisplayName`. */
    userIdToDisplayName?: Record<string, string>;

    fwLabel?: string;
    battLabel?: string;
    raceLogLabel?: string;
    debugLogLabel?: string;
    lastHeardLabel?: string;
    reasonLabel?: string;
};